<template>
  <GenericModal
    v-model="modelValue"
    :title="title"
    :closable="closable"
    :close-on-backdrop="closeOnBackdrop"
    @close="handleClose"
  >
    <template #body>
      <div class="tw-text-center">
        <!-- Icon -->
        <div
          v-if="type === 'warning'"
          class="tw-mx-auto tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-rounded-full tw-bg-yellow-100 tw-mb-4"
        >
          <svg
            class="tw-w-6 tw-h-6 tw-text-yellow-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 6.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <div
          v-else-if="type === 'danger'"
          class="tw-mx-auto tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-rounded-full tw-bg-red-100 tw-mb-4"
        >
          <svg
            class="tw-w-6 tw-h-6 tw-text-red-600"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            color="currentColor"
            fill="none"
          >
            <path
              d="M13.9248 21H10.0752C5.44476 21 3.12955 21 2.27636 19.4939C1.42317 17.9879 2.60736 15.9914 4.97574 11.9985L6.90057 8.75333C9.17559 4.91778 10.3131 3 12 3C13.6869 3 14.8244 4.91777 17.0994 8.75332L19.0243 11.9985C21.3926 15.9914 22.5768 17.9879 21.7236 19.4939C20.8704 21 18.5552 21 13.9248 21Z"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M12 17V12.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M12 8.99828V8.98828"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
        </div>

        <div
          v-else-if="type === 'info'"
          class="tw-mx-auto tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-rounded-full tw-bg-blue-100 tw-mb-4"
        >
          <svg
            class="tw-w-6 tw-h-6 tw-text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            color="currentColor"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></circle>
            <path
              d="M12 16V11.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M12 8.01172V8.00172"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
        </div>

        <div
          v-else-if="type === 'success'"
          class="tw-mx-auto tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-rounded-full tw-bg-green-100 tw-mb-4"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="tw-w-6 tw-h-6 tw-text-green-600"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            color="currentColor"
            fill="none"
          >
            <path
              d="M17 3.33782C15.5291 2.48697 13.8214 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 11.3151 21.9311 10.6462 21.8 10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            ></path>
            <path
              d="M8 12.5C8 12.5 9.5 12.5 11.5 16C11.5 16 17.0588 6.83333 22 5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
        </div>

        <!-- Message -->
        <div class="tw-mb-6">
          <slot name="message">
            <p class="tw-text-gray-700 tw-text-base">
              {{ message }}
            </p>
          </slot>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-3">
        <button @click="handleCancel" :class="cancelButtonClass" type="button">
          {{ cancelText }}
        </button>
        <button @click="handleConfirm" :class="confirmButtonClass" type="button">
          {{ confirmText }}
        </button>
      </div>
    </template>
  </GenericModal>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import GenericModal from './GenericModal.vue';

interface Props {
  modelValue: boolean;
  title?: string;
  message?: string;
  type?: 'warning' | 'danger' | 'info' | 'success';
  confirmText?: string;
  cancelText?: string;
  closable?: boolean;
  closeOnBackdrop?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'confirm'): void;
  (e: 'cancel'): void;
  (e: 'close'): void;
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Confirmation',
  message: 'Are you sure you want to proceed?',
  type: 'warning',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  closable: true,
  closeOnBackdrop: false,
});

const emit = defineEmits<Emits>();

const modelValue = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const confirmButtonClass = computed(() => {
  const baseClass =
    'tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-rounded-md tw-transition-colors';

  switch (props.type) {
    case 'danger':
      return `${baseClass} tw-bg-red-600 hover:tw-bg-red-700`;
    case 'info':
    case 'warning':
    default:
      return `${baseClass} tw-bg-secondary-500 hover:tw-bg-secondary-700`;
  }
});

const cancelButtonClass = computed(() => {
  const baseClass =
    'tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-transition-colors tw-bg-white tw-border tw-border-solid';

  switch (props.type) {
    case 'danger':
      return `${baseClass} tw-border-red-600 hover:tw-bg-red-600 hover:tw-text-white tw-text-red-600`;
    case 'info':
    case 'warning':
    default:
      return `${baseClass} tw-border-secondary-500 hover:tw-bg-secondary-500 hover:tw-text-white tw-text-secondary-500`;
  }
});

const handleConfirm = () => {
  emit('confirm');
  emit('update:modelValue', false);
};

const handleCancel = () => {
  emit('cancel');
  emit('update:modelValue', false);
};

const handleClose = () => {
  emit('close');
  emit('update:modelValue', false);
};
</script>

<style scoped>
/* All styling is handled via Tailwind classes */
</style>
